import AddIcon from '@mui/icons-material/Add';
import LocalPrintshopRoundedIcon from '@mui/icons-material/LocalPrintshopRounded';
import CheckBoxOutlineBlankOutlinedIcon from '@mui/icons-material/CheckBoxOutlineBlankOutlined';
import CheckBoxOutlinedIcon from '@mui/icons-material/CheckBoxOutlined';
import FilterListIcon from '@mui/icons-material/FilterList';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded';
import { Box, Button, IconButton, Skeleton, Stack, Tooltip } from '@mui/material';
import { FC, memo, useRef, useState } from 'react';
import ReactGA from 'react-ga4';
import TruckDisplayStatusMenu from 'src/components/TruckDisplayStatusMenu';
import { SCREEN_NAMES } from 'src/constants/screenNames';
import { PlanningsGroupEntity } from 'src/entities/PlanningsGroup.entity';
import { TruckDisplayStatusVo } from 'src/vo/TruckDisplayStatusVo';

import { VehicleRowGroup } from './adapter';
import { DisplayMetric } from './DisplayMetricToggle';
import { VehicleInfoView } from './VehicleInfoView';

type Props = {
  rowGroups: VehicleRowGroup[];
  isLoading: boolean;
  displayMetrics: Set<DisplayMetric>;
  selectedRowIds: Set<string>;
  allSelected: boolean;
  /** 車両表示ステータス (V3Plans の TruckSelectMenuPresenter 互換) */
  truckDisplayStatus: TruckDisplayStatusVo;
  onTruckDisplayStatusChange: (status: TruckDisplayStatusVo) => void;
  hasSelection: boolean;
  inactiveVehicleIds: Set<string>;
  /** id=0「すべて表示する」を先頭に含むグループ全体。空ならグループ UI を出さない */
  truckGroupEntitiesWithAll: PlanningsGroupEntity[];
  /** 現在チェック済みのグループ index 群 */
  selectedTruckGroupIndices: number[];
  onSelectedTruckGroupIndicesChange: (next: number[]) => void;
  onRowToggle: (rowId: string) => void;
  onSelectAll: () => void;
  onDeselectAll: () => void;
  /** 選択中の行の案件をまとめて未割当に戻す */
  onUnassignSelected: () => void;
  onAddVehicle: () => void;
  onToggleVehicleEnabled: (truckId: number) => void;
  onAddRotation: (truckId: number) => void;
  onDeleteRotation: (truckId: number, rotationNumber: number) => void;
  /** 車両名 / ドライバー名 / 車番 クリック時のハンドラ。Presenter 側でメニューを開いて mutation を発火する */
  onTruckEdit?: (truckId: number, anchor: HTMLElement) => void;
  onDriverEdit?: (truckId: number, anchor: HTMLElement) => void;
  onPlateNumberEdit?: (truckId: number, anchor: HTMLElement) => void;
  /** 帳票出力 (V3Plans の SelectPrintPresenter と同じダイアログ) を開く */
  onOpenPrint?: () => void;
};

const ConvenienceDispatchVehicleList: FC<Props> = memo(({
  rowGroups,
  isLoading,
  displayMetrics,
  selectedRowIds,
  allSelected,
  truckDisplayStatus,
  onTruckDisplayStatusChange,
  hasSelection,
  inactiveVehicleIds,
  truckGroupEntitiesWithAll,
  selectedTruckGroupIndices,
  onSelectedTruckGroupIndicesChange,
  onRowToggle,
  onSelectAll,
  onDeselectAll,
  onUnassignSelected,
  onAddVehicle,
  onToggleVehicleEnabled,
  onAddRotation,
  onDeleteRotation,
  onTruckEdit,
  onDriverEdit,
  onPlateNumberEdit,
  onOpenPrint,
}) => {
  const filterButtonRef = useRef<HTMLButtonElement>(null);
  const [filterMenuOpen, setFilterMenuOpen] = useState(false);

  // フィルタボタンのラベル: ステータスが「すべて表示」以外 > 部分選択中のグループ名 > "すべて"
  const partialGroupSelection = truckGroupEntitiesWithAll.length > 0
    && !selectedTruckGroupIndices.includes(0);
  const filterLabel = truckDisplayStatus !== 'すべて表示'
    ? truckDisplayStatus
    : partialGroupSelection
      ? selectedTruckGroupIndices.length === 1
        ? truckGroupEntitiesWithAll[selectedTruckGroupIndices[0]]?.name ?? 'すべて'
        : `${selectedTruckGroupIndices.length} グループ`
      : 'すべて';

  if (isLoading) {
    return (
      <Stack spacing={1} sx={{ p: 1 }}>
        {Array.from({ length: 5 }, (_, i) => (
          <Skeleton key={i} variant="rectangular" height={72} />
        ))}
      </Stack>
    );
  }

  return (
    <Box>
      {/* 固定ヘッダ (空状態でも常に表示) */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 30,
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'hsl(var(--secondary) / 0.7)',
        }}
      >
        {/* Row 1: フィルタ + 車両追加 */}
        <Box sx={{ display: 'flex', height: 40, alignItems: 'center', justifyContent: 'space-between', px: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Button
              ref={filterButtonRef}
              size="small"
              variant="outlined"
              startIcon={<FilterListIcon sx={{ fontSize: 12 }} />}
              endIcon={<KeyboardArrowDownIcon sx={{ fontSize: 14 }} />}
              onClick={() => {
                ReactGA.event('click', {
                  screen_name: SCREEN_NAMES.CONVENIENCE_DISPATCHES,
                  button_name: '車両表示ステータス フィルタ',
                });
                setFilterMenuOpen(true);
              }}
              sx={{ height: 28, px: 1, fontSize: 12, textTransform: 'none', minWidth: 0 }}
            >
              {filterLabel}
            </Button>
            <TruckDisplayStatusMenu
              anchorEl={filterButtonRef.current}
              open={filterMenuOpen}
              onClose={() => setFilterMenuOpen(false)}
              truckDisplayStatus={truckDisplayStatus}
              onTruckDisplayStatusChange={(status) => {
                ReactGA.event('select', {
                  screen_name: SCREEN_NAMES.CONVENIENCE_DISPATCHES,
                  button_name: '車両表示ステータス',
                  label: String(status),
                });
                onTruckDisplayStatusChange(status);
              }}
              hasSelection={hasSelection}
              groupEntities={truckGroupEntitiesWithAll}
              selectedGroupIndices={selectedTruckGroupIndices}
              onSelectedGroupIndicesChange={(next) => {
                ReactGA.event('select', {
                  screen_name: SCREEN_NAMES.CONVENIENCE_DISPATCHES,
                  button_name: '車両グループ選択',
                  label: next.join(','),
                });
                onSelectedTruckGroupIndicesChange(next);
              }}
            />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<AddIcon sx={{ fontSize: 14 }} />}
              onClick={() => {
                ReactGA.event('click', {
                  screen_name: SCREEN_NAMES.CONVENIENCE_DISPATCHES,
                  button_name: '車両追加',
                });
                onAddVehicle();
              }}
              sx={{ height: 28, px: 1, fontSize: 12, textTransform: 'none', minWidth: 0, whiteSpace: 'nowrap' }}
            >
              車両追加
            </Button>
            {onOpenPrint && (
              <Tooltip title="配送指示書を出力する" arrow>
                <IconButton
                  size="small"
                  onClick={() => {
                    ReactGA.event('click', {
                      screen_name: SCREEN_NAMES.CONVENIENCE_DISPATCHES,
                      button_name: '帳票出力',
                    });
                    onOpenPrint();
                  }}
                  aria-label="帳票出力"
                  sx={{ p: 0.5, color: 'text.primary' }}
                >
                  <LocalPrintshopRoundedIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>

        {/* Row 2: 全選択 + 選択件数 */}
        <Box sx={{ display: 'flex', height: 32, alignItems: 'center', gap: 0.75, borderTop: '1px solid hsl(var(--border) / 0.4)', px: 1.5 }}>
          <Button
            size="small"
            variant="text"
            startIcon={allSelected
              ? <CheckBoxOutlinedIcon sx={{ fontSize: 12, color: 'primary.main' }} />
              : <CheckBoxOutlineBlankOutlinedIcon sx={{ fontSize: 12 }} />}
            onClick={() => {
              ReactGA.event('click', {
                screen_name: SCREEN_NAMES.CONVENIENCE_DISPATCHES,
                button_name: '車両 全選択',
                label: allSelected ? 'OFF' : 'ON',
              });
              if (allSelected) onDeselectAll(); else onSelectAll();
            }}
            sx={{ height: 24, px: 0.75, fontSize: 10, textTransform: 'none', minWidth: 0, color: 'text.primary' }}
          >
            {allSelected ? '全解除' : '全選択'}
          </Button>
          {selectedRowIds.size > 0 && (
            <>
              <Box
                component="span"
                sx={{
                  borderRadius: '9999px',
                  bgcolor: 'hsl(var(--primary) / 0.1)',
                  px: 1,
                  py: '2px',
                  fontSize: 10,
                  fontWeight: 600,
                  color: 'hsl(var(--primary))',
                }}
              >
                {selectedRowIds.size}件選択中
              </Box>
              <Box sx={{ mx: 0.5, height: 16, width: '1px', bgcolor: 'hsl(var(--border))' }} />
              <Button
                variant="text"
                size="small"
                color="error"
                startIcon={<RestartAltRoundedIcon sx={{ fontSize: 12 }} />}
                onClick={() => {
                  ReactGA.event('click', {
                    screen_name: SCREEN_NAMES.CONVENIENCE_DISPATCHES,
                    button_name: '未割当に戻す',
                  });
                  onUnassignSelected();
                }}
                sx={{ height: 24, px: 0.75, fontSize: 10, textTransform: 'none', minWidth: 0 }}
              >
                未割当に戻す
              </Button>
            </>
          )}
        </Box>
      </Box>

      {rowGroups.map(({ vehicle, rows }, groupIndex) => {
        const vehicleEnabled = !inactiveVehicleIds.has(vehicle.id);
        const isLastGroup = groupIndex === rowGroups.length - 1;

        return (
          <Box key={`${vehicle.id}-info`}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '1px',
                bgcolor: 'hsl(var(--secondary) / 0.2)',
                px: 1,
                py: 0.75,
              }}
            >
              {rows.map((row, rowIndex) => {
                const groupPosition = rows.length === 1
                  ? 'single'
                  : rowIndex === 0
                    ? 'start'
                    : rowIndex === rows.length - 1
                      ? 'end'
                      : 'middle';
                const rowRadius = groupPosition === 'single'
                  ? '12px'
                  : groupPosition === 'start'
                    ? '12px 12px 0 0'
                    : groupPosition === 'end'
                      ? '0 0 12px 12px'
                      : '0';
                const isSelected = selectedRowIds.has(row.rowId);

                return (
                  <Box
                    key={`${row.rowId}-info`}
                    role="button"
                    tabIndex={0}
                    onClick={() => {
                      ReactGA.event('click', {
                        screen_name: SCREEN_NAMES.CONVENIENCE_DISPATCHES,
                        button_name: '車両行 選択',
                        label: isSelected ? 'OFF' : 'ON',
                      });
                      onRowToggle(row.rowId);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        ReactGA.event('click', {
                          screen_name: SCREEN_NAMES.CONVENIENCE_DISPATCHES,
                          button_name: '車両行 選択',
                          label: isSelected ? 'OFF' : 'ON',
                        });
                        onRowToggle(row.rowId);
                      }
                    }}
                    sx={{
                      height: 72,
                      overflow: 'hidden',
                      cursor: 'pointer',
                      transition: 'all 0.15s',
                      borderRadius: rowRadius,
                      bgcolor: isSelected ? 'hsl(var(--primary) / 0.05)' : 'hsl(var(--secondary) / 0.1)',
                      ...(isSelected
                        ? { boxShadow: 'inset 0 0 0 2px hsl(var(--primary))' }
                        : { '&:hover': { bgcolor: 'hsl(var(--secondary) / 0.2)' } }),
                    }}
                  >
                    <VehicleInfoView
                      vehicle={vehicle}
                      cards={row.cards}
                      rotationNumber={row.rotationNumber}
                      enabled={vehicleEnabled}
                      displayMetrics={displayMetrics}
                      canAddRound={rowIndex === rows.length - 1}
                      canDeleteRound={rows.length > 1}
                      onToggleEnabled={() => onToggleVehicleEnabled(row.truckId)}
                      onAddRound={() => onAddRotation(row.truckId)}
                      onDeleteRound={() => onDeleteRotation(row.truckId, row.rotationNumber)}
                      onTruckClick={onTruckEdit ? (anchor) => onTruckEdit(row.truckId, anchor) : undefined}
                      onDriverClick={onDriverEdit ? (anchor) => onDriverEdit(row.truckId, anchor) : undefined}
                      onPlateNumberClick={onPlateNumberEdit ? (anchor) => onPlateNumberEdit(row.truckId, anchor) : undefined}
                    />
                  </Box>
                );
              })}
            </Box>
            {!isLastGroup && <Box sx={{ mx: 1, borderBottom: '1px solid hsl(var(--border) / 0.4)' }} />}
          </Box>
        );
      })}
    </Box>
  );
});

export default ConvenienceDispatchVehicleList;
